/**
 * api.js — SimplifyQR API helper
 * ───────────────────────────────
 * All API calls go through your Cloudflare Worker proxy.
 * No API keys ever in the browser.
 *
 * Copy to: src/utils/api.js
 *
 * Add to .env:
 *   REACT_APP_WORKER_URL=https://simplifyqr-proxy.YOUR-SUBDOMAIN.workers.dev
 */


const WORKER_URL = process.env.REACT_APP_WORKER_URL || '';

// ── Health check ──────────────────────────────────────────────
export async function checkWorkerHealth() {
  try {
    const res  = await fetch(`${WORKER_URL}/api/health`);
    const data = await res.json();
    return data;
  } catch { return null; }
}

// ── A: OpenRouter AI ──────────────────────────────────────────
export async function callOpenRouter({ messages, model = 'openrouter/free', max_tokens = 300, response_format }) {
  if (!WORKER_URL) return null;
  try {
    const body = { model, messages, max_tokens, temperature: 0.7 };
    if (response_format) body.response_format = response_format;

    const res  = await fetch(`${WORKER_URL}/api/ai`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.choices?.[0]?.message?.content?.trim() || null;
  } catch { return null; }
}

// ── B: URL Expander ───────────────────────────────────────────
const SHORTENERS = ['bit.ly','tinyurl.com','rb.gy','cutt.ly','t.co','goo.gl','ow.ly','short.io','tiny.cc','is.gd'];

export async function expandUrl(url) {
  if (!WORKER_URL) return null;
  const isShort = SHORTENERS.some(s => url.includes(s));
  if (!isShort) return null;
  try {
    const res  = await fetch(`${WORKER_URL}/api/expand?url=${encodeURIComponent(url)}`);
    const data = await res.json();
    return data?.expanded || null;
  } catch { return null; }
}

// ── C: Google Safe Browsing ───────────────────────────────────
export async function checkSafeBrowsing(url) {
  if (!WORKER_URL) return null;
  try {
    const res  = await fetch(`${WORKER_URL}/api/safe`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client: { clientId: 'simplifyqr', clientVersion: '1.0' },
        threatInfo: {
          threatTypes:      ['MALWARE', 'SOCIAL_ENGINEERING', 'UNWANTED_SOFTWARE', 'POTENTIALLY_HARMFUL_APPLICATION'],
          platformTypes:    ['ANY_PLATFORM'],
          threatEntryTypes: ['URL'],
          threatEntries:    [{ url }],
        },
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.matches?.length > 0
      ? { safe: false, threats: data.matches.map(m => m.threatType) }
      : { safe: true, threats: [] };
  } catch { return null; }
}

// ── Gemini AI (alternative) ───────────────────────────────────
export async function callGemini({ contents, model = 'gemini-1.5-flash', max_tokens = 300 }) {
  if (!WORKER_URL) return null;
  try {
    const res  = await fetch(`${WORKER_URL}/api/gemini`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        contents,
        generationConfig: { temperature: 0.7, maxOutputTokens: max_tokens },
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || null;
  } catch { return null; }
}